import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import {
    DEFAULT_AI_PANEL_POSITION,
    MOBILE_BREAKPOINT,
    PANEL_FALLBACK_HEIGHT,
    PANEL_FALLBACK_WIDTH,
    PANEL_TRIGGER_GAP,
    PANEL_VIEWPORT_MARGIN,
} from './constants';
import type { AIPanelPosition, AIStatus } from './types';

type UseFloatingPanelPositionParams = {
    mode: 'floating' | 'inline';
    isAIPanelOpen: boolean;
    isAIPanelRendered: boolean;
    streamedText: string;
    aiStatus: AIStatus;
    aiTriggerRef: RefObject<HTMLButtonElement>;
    aiPanelRef: MutableRefObject<HTMLDivElement | null>;
    onClose: () => void;
    setIsAIPanelVisible: (value: boolean) => void;
};

export const useFloatingPanelPosition = ({
    mode,
    isAIPanelOpen,
    isAIPanelRendered,
    streamedText,
    aiStatus,
    aiTriggerRef,
    aiPanelRef,
    onClose,
    setIsAIPanelVisible,
}: UseFloatingPanelPositionParams): AIPanelPosition => {
    const [aiPanelPosition, setAiPanelPosition] =
        useState<AIPanelPosition>(DEFAULT_AI_PANEL_POSITION);

    const updateAiPanelPosition = useCallback(() => {
        if (mode === 'inline') return;
        if (!isAIPanelOpen) return;

        const trigger = aiTriggerRef.current;
        if (!trigger) return;

        const isMobile = window.innerWidth < MOBILE_BREAKPOINT;

        if (isMobile) {
            setAiPanelPosition((prev) => ({ ...prev, isMobile: true }));
            return;
        }

        const triggerRect = trigger.getBoundingClientRect();
        const panelWidth = aiPanelRef.current?.offsetWidth || PANEL_FALLBACK_WIDTH;
        const panelHeight = aiPanelRef.current?.offsetHeight || PANEL_FALLBACK_HEIGHT;

        let left = triggerRect.right - panelWidth;
        left = Math.max(
            PANEL_VIEWPORT_MARGIN,
            Math.min(left, window.innerWidth - panelWidth - PANEL_VIEWPORT_MARGIN),
        );

        let placement: 'top' | 'bottom' = 'bottom';
        let top = triggerRect.bottom + PANEL_TRIGGER_GAP;

        if (top + panelHeight + PANEL_VIEWPORT_MARGIN > window.innerHeight) {
            top = triggerRect.top - panelHeight - PANEL_TRIGGER_GAP;
            placement = 'top';
        }

        if (top < PANEL_VIEWPORT_MARGIN) {
            top = PANEL_VIEWPORT_MARGIN;
            placement = 'bottom';
        }

        setAiPanelPosition({
            top,
            left,
            placement,
            isMobile: false,
        });
    }, [aiPanelRef, aiTriggerRef, isAIPanelOpen, mode]);

    useLayoutEffect(() => {
        if (mode !== 'inline') return;

        setIsAIPanelVisible(isAIPanelOpen && isAIPanelRendered);
    }, [isAIPanelOpen, isAIPanelRendered, mode, setIsAIPanelVisible]);

    useLayoutEffect(() => {
        if (mode === 'inline') return;
        if (!isAIPanelOpen || !isAIPanelRendered) return;

        updateAiPanelPosition();

        const animationFrame = requestAnimationFrame(() => {
            updateAiPanelPosition();
            setIsAIPanelVisible(true);
        });

        return () => {
            cancelAnimationFrame(animationFrame);
        };
    }, [isAIPanelOpen, isAIPanelRendered, mode, setIsAIPanelVisible, updateAiPanelPosition]);

    useEffect(() => {
        if (mode === 'inline' || !isAIPanelOpen) return;

        const handleViewportChange = () => {
            updateAiPanelPosition();
        };

        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('scroll', handleViewportChange, true);

        return () => {
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('scroll', handleViewportChange, true);
        };
    }, [isAIPanelOpen, mode, updateAiPanelPosition]);

    useEffect(() => {
        if (mode === 'inline' || !isAIPanelOpen) return;
        updateAiPanelPosition();
    }, [aiStatus, isAIPanelOpen, mode, streamedText, updateAiPanelPosition]);

    useEffect(() => {
        if (mode === 'inline' || !isAIPanelOpen) return;

        const handleEscClose = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (aiPanelRef.current?.contains(target)) return;
            if (aiTriggerRef.current?.contains(target)) return;

            onClose();
        };

        document.addEventListener('keydown', handleEscClose);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('keydown', handleEscClose);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [aiPanelRef, aiTriggerRef, isAIPanelOpen, mode, onClose]);

    return aiPanelPosition;
};
